import React, { useState } from "react";
import { Box, Divider, Typography } from "@material-ui/core";
import {
  ExperienceBadge,
  ExperiencesAgeBandEnum,
  ExperiencesPriceBreakdown,
  RewardsAccount,
} from "redmond";
import {
  AvailabilityReviewRating,
  AvailabilityDuration,
  AvailabilityLanguage,
  ExperienceTag,
  Icon,
  IconName,
  ActionLink,
  AvailabilityTicket,
  SanitizedHtml,
} from "halifax";
import clsx from "clsx";
import {
  AVAILABLE,
  getExperimentVariant,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../../../context/experiments";

import { ShopComponentSkeleton } from "../../component";
import styles from "./styles.module.scss";
import * as textConstants from "../../textConstants";
import * as commonConstants from "../../../common/textConstants";
import { IShopOverview } from "../ShopOverview/component";
import {
  IShopCancellationPolicyProps,
  ShopCancellationPolicy,
} from "../ShopCancellationPolicy/component";
import { ExperiencesMobilePopoverCard } from "../../../common/ExperiencesMobilePopoverCard";
import { ParsedAgeBand } from "../../reducer";

export interface IShopMobileHeaderCardProps extends IShopOverview {
  isFreeCancellation?: boolean;
  pricingBreakdown?: ExperiencesPriceBreakdown;
  largestValueAccount?: RewardsAccount;
  parsedAgeBands?: { [key in ExperiencesAgeBandEnum]?: ParsedAgeBand };
  cancellationPolicyProps: IShopCancellationPolicyProps | null;
}

const Skeleton = () => (
  <Box className={styles["experiences-shop-skeleton"]}>
    <Box className={styles["section-wrapper"]}>
      <ShopComponentSkeleton name="small" />
      <ShopComponentSkeleton name="large" />
      <ShopComponentSkeleton name="medium" />
    </Box>
    <Box className={styles["section-wrapper"]}>
      <ShopComponentSkeleton name="medium" />
      <ShopComponentSkeleton name="large" />
      <ShopComponentSkeleton name="medium" />
    </Box>
    <Box className={styles["section-wrapper"]}>
      <ShopComponentSkeleton name="large" />
      <ShopComponentSkeleton name="small" />
      <ShopComponentSkeleton name="large" />
    </Box>
    <Box className={styles["section-wrapper"]}>
      <ShopComponentSkeleton name="medium" />
      <ShopComponentSkeleton name="small" />
      <ShopComponentSkeleton name="medium" />
    </Box>
  </Box>
);

export const ShopMobileHeaderCard = (props: IShopMobileHeaderCardProps) => {
  const {
    isSkeleton = false,
    title,
    rating,
    duration,
    durationFromMinutes,
    durationToMinutes,
    languageGuides,
    badges,
    experienceMaxTravelers,
    isFreeCancellation,
    pricingBreakdown,
    largestValueAccount,
    parsedAgeBands,
    cancellationPolicyProps,
    ticket,
    pricingCategory,
  } = props;

  const expState = useExperiments();
  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const [isBadgeExcellenceModalOpen, setBadgeExcellenceModalOpen] =
    useState(false);

  const [openCancellationPolicyModal, setOpenCancellationPolicyModal] =
    useState(false);

  if (!title || !rating || duration === undefined || !languageGuides) {
    return null;
  }

  const { priceTitle, rewardsTitle } = textConstants.getPriceRewardTitles(
    pricingBreakdown,
    parsedAgeBands,
    pricingCategory,
    largestValueAccount
  );

  let subtitle = experienceMaxTravelers
    ? textConstants.MAX_TRAVELER_TEXT(experienceMaxTravelers)
    : undefined;

  if (
    parsedAgeBands?.Adult?.priceVariesByCount ||
    parsedAgeBands?.Traveler?.priceVariesByCount
  ) {
    subtitle = textConstants.PRICE_VARIES_ON_GROUP_SIZE;
  }

  return (
    <Box className={styles["experiences-shop-mobile-header-card"]}>
      <Typography
        className="experiences-shop-mobile-header-card-title"
        variant="h6"
      >
        {title}
      </Typography>
      <Box className={styles["experiences-shop-mobile-header-card-subtext"]}>
        <AvailabilityReviewRating
          reviewCount={rating.numberOfReviews}
          scaledScore={rating.reviewAverage}
          shortReviews
        />
        {duration && (
          <>
            <Divider
              className={
                styles["experiences-shop-mobile-header-card-subtext-dividers"]
              }
              orientation="vertical"
            />
            <AvailabilityDuration
              duration={duration}
              durationFromMinutes={durationFromMinutes}
              durationToMinutes={durationToMinutes}
              isSkeleton={isSkeleton}
            />
          </>
        )}
        {languageGuides && languageGuides.length > 0 && (
          <>
            <Divider
              className={
                styles["experiences-shop-mobile-header-card-subtext-dividers"]
              }
              orientation="vertical"
            />
            <AvailabilityLanguage
              languages={languageGuides}
              isSkeleton={isSkeleton}
              mobile
            />
          </>
        )}
        {ticket && (
          <>
            <Divider
              className={
                styles["experiences-shop-mobile-header-card-subtext-dividers"]
              }
              orientation="vertical"
            />
            <AvailabilityTicket ticket={ticket} />
          </>
        )}
      </Box>
      <SanitizedHtml
        as={Typography}
        className={styles["experiences-shop-mobile-header-card-price-title"]}
        html={textConstants.getPriceAndRewardsCopy({
            price: priceTitle,
            rewards: rewardsTitle,
          })}
      />
      {subtitle && (
        <Typography
          className={
            styles["experiences-shop-mobile-header-card-price-subtitle"]
          }
          variant="body2"
        >
          {subtitle}
        </Typography>
      )}
      {isFreeCancellation && (
        <Box
          className={clsx(
            styles["experiences-shop-mobile-header-card-cancellation-policy"],
            { [styles.xmod]: isXmodRedesign }
          )}
        >
          <Typography
            className={clsx(
              styles[
                "experiences-shop-mobile-header-card-cancellation-policy-text"
              ],
              { [styles.xmod]: isXmodRedesign }
            )}
          >
            <Icon name={IconName.CheckCircleFilledGreen} />
            {commonConstants.FREE_CANCELLATION}
          </Typography>
          <ActionLink
            onClick={() => {
              setOpenCancellationPolicyModal(true);
            }}
            className={
              styles[
                "experiences-shop-mobile-header-card-cancellation-policy-button"
              ]
            }
            content={
              <Typography
                className={
                  styles[
                    "experiences-shop-mobile-header-card-cancellation-policy-text"
                  ]
                }
              >
                {commonConstants.VIEW_POLICY_DETAILS}
                <Icon name={IconName.InfoCircle} />
              </Typography>
            }
          />
        </Box>
      )}
      {badges && badges.length > 0 && (
        <Box
          className={
            styles["experience-shop-mobile-header-card-tags-container"]
          }
        >
          {badges.map((badge) => {
            // Shop cancellation policy banner instead of cancellation policy tag on mobile
            if (badge === ExperienceBadge.FreeCancellation) {
              return null;
            }
            return (
              <ExperienceTag
                key={badge}
                badge={badge}
                onBadgeClick={
                  badge === ExperienceBadge.BadgeOfExcellence
                    ? () => {
                        if (!isBadgeExcellenceModalOpen)
                          setBadgeExcellenceModalOpen(true);
                      }
                    : undefined
                }
              />
            );
          })}
        </Box>
      )}
      {cancellationPolicyProps && (
        <ExperiencesMobilePopoverCard
          headerText={commonConstants.CANCELLATION_POLICY}
          content={<ShopCancellationPolicy {...cancellationPolicyProps} />}
          isOpen={openCancellationPolicyModal}
          setOpen={setOpenCancellationPolicyModal}
        />
      )}
      {isSkeleton ? <Skeleton /> : null}
    </Box>
  );
};
